import * as React from "react";
import { useLayoutEffect, useRef } from "react";

import { Input } from "@/components/ui/input";
import { formatBRLInput, parseBRLCentsInput } from "@/lib/format";

/**
 * Campo de valor em reais, o MESMO em todo o app (pedido do Rick, 29/10/2026:
 * "todos os lugares onde tem entrada de valores monetários").
 *
 * Máscara ao vivo, estilo app de banco: cada dígito entra como centavo e
 * empurra o valor para a esquerda (1,0,0,0,0,0 → R$ 1.000,00) — ver
 * `parseBRLCentsInput`. O "R$ 0,00" em cinza no campo vazio mostra o formato
 * antes da primeira tecla.
 *
 * Dois cuidados que um input controlado comum não tem:
 *  1. Reformatar o texto a cada tecla faz o navegador pular o cursor; ele é
 *     devolvido ao fim, senão a tecla seguinte não continua de onde parou.
 *  2. Uma máscara "R$ X,XX" fixa (`parseBRLInput`) não serve: dígito depois da
 *     vírgula só mexe nos centavos e é arredondado, nunca desloca o número.
 *
 * `negativo` (saldo inicial de caixa): um "-" em qualquer ponto do texto
 * inverte o sinal; sem ele, o valor é sempre positivo.
 */
export type MoedaInputProps = Omit<React.ComponentProps<typeof Input>, "value" | "onChange" | "type" | "defaultValue"> & {
  value: number | null | undefined;
  onValueChange: (value: number | null) => void;
  negativo?: boolean;
};

export const MoedaInput = React.forwardRef<HTMLInputElement, MoedaInputProps>(
  ({ value, onValueChange, negativo = false, placeholder = "R$ 0,00", ...rest }, ref) => {
    const interno = useRef<HTMLInputElement | null>(null);
    const abs = value == null || !Number.isFinite(value) ? null : Math.abs(value);
    const display = abs == null ? "" : `${negativo && (value ?? 0) < 0 ? "-" : ""}${formatBRLInput(abs)}`;

    useLayoutEffect(() => {
      const el = interno.current;
      if (el && document.activeElement === el) {
        const end = el.value.length;
        el.setSelectionRange(end, end);
      }
    }, [display]);

    return (
      <Input
        {...rest}
        ref={(el) => {
          interno.current = el;
          if (typeof ref === "function") ref(el);
          else if (ref) ref.current = el;
        }}
        type="text"
        inputMode={negativo ? "text" : "numeric"}
        placeholder={placeholder}
        value={display}
        onChange={(e) => {
          const n = parseBRLCentsInput(e.target.value);
          const menos = negativo && e.target.value.includes("-");
          onValueChange(n == null ? null : menos ? -n : n);
        }}
      />
    );
  },
);
MoedaInput.displayName = "MoedaInput";

/**
 * O mesmo campo para telas que guardam o valor como TEXTO numérico ("1234.56",
 * vazio = sem valor) — as modais do fluxo de caixa e de comissões validam esse
 * texto depois, e assim nada muda nelas além do campo.
 */
export const MoedaTextoInput = ({
  value,
  onValueChange,
  ...rest
}: Omit<MoedaInputProps, "value" | "onValueChange"> & { value: string; onValueChange: (value: string) => void }) => {
  const n = value.trim() === "" ? null : Number(value);
  return (
    <MoedaInput
      {...rest}
      value={n != null && Number.isFinite(n) ? n : null}
      onValueChange={(v) => onValueChange(v == null ? "" : String(v))}
    />
  );
};
